import { useSystemInfo, useSystemViewData } from "../providers/helpers/useSystem"
import { Skeleton } from "@chakra-ui/react"
import { useMemo } from "react"
import HybridDownload from "@hydrologiq/shared/reporting/HybridDownload"
import InstallationReporting from "../components/reporting/InstallationReporting"
import Usage from "@hydrologiq/shared/reporting/Usage"
import { type RowType } from "@hydrologiq/shared/reporting/rows"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"

const SystemReporting = () => {
  const info = useSystemInfo()
  const systemViewData = useSystemViewData()
  const source = systemViewData?.system?.source ?? "timescale"

  let route = useRoute()
  route = route.endsWith("/") ? route : `${route}/`

  const systemId = systemViewData?.system?.id
  const systemName = systemViewData?.system?.name

  // Detect if the system contains an electrolyser (used to hide misleading hydrogen weight columns from CSV)
  const hasElectrolyser = useMemo(() => {
    if (!systemViewData) return false
    for (const group of systemViewData.installations ?? []) {
      for (const node of group.nodes ?? []) {
        if ((node.asset as any)?.class === "electrolyser") return true
      }
    }
    for (const node of systemViewData.isolatedNodes ?? []) {
      if ((node.asset as any)?.class === "electrolyser") return true
    }
    return false
  }, [systemViewData])

  // Derive installation date bounds (earliest start, latest end across all installations)
  const installations = systemViewData?.installations ?? []
  let minDate: string | undefined
  let maxDate: string | undefined

  for (const group of installations) {
    const inst = group.installation
    if (inst.start_date) {
      const startStr = inst.start_date.split("T")[0]
      if (!minDate || startStr < minDate) minDate = startStr
    }
    if (inst.end_date) {
      const endStr = inst.end_date.split("T")[0]
      if (!maxDate || endStr > maxDate) maxDate = endStr
    }
  }

  // If source is "timescale" (default), use the shared HybridDownload.
  // Otherwise fall back to the legacy InstallationReporting.
  if (source === "timescale" && systemId && systemName) {
    return (
      <HybridDownload
        scope="systems"
        id={systemId}
        name={systemName}
        minDate={minDate}
        maxDate={maxDate}
        hasElectrolyser={hasElectrolyser}
      />
    )
  }

  // Legacy fallback: use the existing GraphQL-based InstallationReporting
  const [installation, installationInfo] =
    info?.inlineMonitoring || !info?.installations
      ? [undefined, undefined]
      : Object.entries(info?.installations)[0]

  const availableFields = info?.reporting?.fields as (keyof RowType)[] | undefined

  return installation && installationInfo ? (
    <Usage
      start={installationInfo.startDate}
      end={installationInfo.endDate}
      UsageComponent={InstallationReporting}
      additionalProps={{ installationId: installation, installationName: installationInfo.name }}
      availableFields={availableFields}
    />
  ) : (
    <Skeleton height="100%" width="100%" />
  )
}

export default SystemReporting
